import React from 'react';
import { Task, MediaType } from '../../../types';
import { TaskCardState } from './TaskCard';
import { TaskIllustration } from './TaskIllustration';
import './MiniTaskCard.css';

export interface MiniTaskCardProps {
  task: Task;
  state: TaskCardState;
  timeLeft?: string;
  /** Deadline passed and task not yet done — colours the dot ember. */
  overdue?: boolean;
  onClick: () => void;
}

const STATUS_COLOR: Record<string, string> = {
  complete: 'var(--sage, #6E7A5A)',
  'just-completed': 'var(--sage, #6E7A5A)',
  urgent: 'var(--ember, #D4715A)',
  needs_redo: 'var(--ember, #D4715A)',
  pending: '#A08870',
  resume: 'var(--ember, #D4715A)',
  active: 'var(--border, #E8E4DF)',
  transition: 'var(--ember, #D4715A)',
};

const STATUS_LABEL: Record<string, string> = {
  complete: 'Done',
  'just-completed': 'Done',
  urgent: 'Urgent',
  needs_redo: 'Redo',
  pending: 'Offline',
  resume: 'In progress',
};

/* Small media-type icons — shown in the info row */
const MEDIA_ICON: Record<string, React.ReactNode> = {
  audio: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 1v22M8 5v14M4 9v6M16 5v14M20 9v6" />
    </svg>
  ),
  photo: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  ),
  video: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="23,7 16,12 23,17" />
      <rect x="1" y="5" width="15" height="14" rx="2" />
    </svg>
  ),
  text: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M4 7V4h16v3M9 20h6M12 4v16" />
    </svg>
  ),
  screen: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  ),
  choice: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 2v8l6-4-6-4zM4 14h16M4 18h10" />
    </svg>
  ),
  'signification-tension': (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="3" y1="12" x2="21" y2="12" /><circle cx="14" cy="12" r="3" fill="currentColor" opacity="0.4" />
    </svg>
  ),
  'signification-triad': (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3L22 21H2Z" />
    </svg>
  ),
  'signification-stones': (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="7" cy="8" r="2.5" /><circle cx="17" cy="7" r="2.5" /><circle cx="12" cy="16" r="2.5" /><circle cx="5" cy="17" r="2" /><circle cx="19" cy="16" r="2" />
    </svg>
  ),
  signification: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3L22 21H2Z" />
    </svg>
  ),
};

export const MiniTaskCard: React.FC<MiniTaskCardProps> = ({ task, state, timeLeft, overdue, onClick }) => {
  const isComplete = state === 'complete' || state === 'just-completed';
  // Dot colour priority: done (green) → overdue (orange) → state default.
  // Keeps the active-with-time-left dot pale grey, as specified.
  const dotColor = isComplete
    ? 'var(--sage, #6E7A5A)'
    : overdue
      ? 'var(--ember, #D4715A)'
      : (STATUS_COLOR[state] || 'var(--border)');
  const isChoice = task.mediaTypes.length > 1;
  const rawType = isChoice ? 'choice' : (task.mediaTypes[0] || MediaType.Audio);
  const mediaType = rawType === 'signification' && (task as any).significationConfig?.items?.[0]?.mode
    ? `signification-${(task as any).significationConfig.items[0].mode}`
    : rawType;

  return (
    <button
      className={`tl-mini ${isComplete ? 'tl-mini--done' : ''} ${state === 'needs_redo' ? 'tl-mini--redo' : ''}`}
      onClick={onClick}
      aria-label={task.title || task.prompt}
    >
      <div className="tl-mini-illust">
        <TaskIllustration type={mediaType as any} className="tc-illus--grid" floating />
        {isComplete && (
          <div className="tl-mini-badge">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round">
              <polyline points="20,6 9,17 4,12" />
            </svg>
          </div>
        )}
      </div>
      <div className="tl-mini-info">
        <span className="tl-mini-title">{task.title || task.prompt}</span>
        <span className="tl-mini-sub">
          <span className="tl-mini-dot" style={{ background: dotColor }} />
          {STATUS_LABEL[state] || ''}
          {timeLeft && state !== 'complete' && state !== 'just-completed' && (
            <>
              {STATUS_LABEL[state] && <span className="tl-mini-sep">&middot;</span>}
              <span className="tl-mini-time">{timeLeft}</span>
            </>
          )}
          <span className="tl-mini-format">{MEDIA_ICON[mediaType] || MEDIA_ICON.audio}</span>
        </span>
      </div>
    </button>
  );
};
